/**
 * AVR-8 Timers
 * Part of AVR8js
 * Reference: http://ww1.microchip.com/downloads/en/DeviceDoc/ATmega48A-PA-88A-PA-168A-PA-328-P-DS-DS40002061A.pdf
 *
 * Copyright (C) 2019, 2020, Uri Shaked
 */
import { avrInterrupt } from '../cpu/interrupt';
import { portBConfig, portDConfig, PinOverrideMode } from './gpio';
const timer01Dividers = {
    0: 0,
    1: 1,
    2: 8,
    3: 64,
    4: 256,
    5: 1024,
    6: 0,
    7: 0,
};
const TOV = 1;
const OCFA = 2;
const OCFB = 4;
const TOIE = 1;
const OCIEA = 2;
const OCIEB = 4;
export const timer0Config = {
    bits: 8,
    captureInterrupt: 0,
    compAInterrupt: 0x1c,
    compBInterrupt: 0x1e,
    ovfInterrupt: 0x20,
    TIFR: 0x35,
    OCRA: 0x47,
    OCRB: 0x48,
    ICR: 0,
    TCNT: 0x46,
    TCCRA: 0x44,
    TCCRB: 0x45,
    TCCRC: 0,
    TIMSK: 0x6e,
    dividers: timer01Dividers,
    compPortA: portDConfig.PORT,
    compPinA: 6,
    compPortB: portDConfig.PORT,
    compPinB: 5,
};
export const timer1Config = {
    bits: 16,
    captureInterrupt: 0x14,
    compAInterrupt: 0x16,
    compBInterrupt: 0x18,
    ovfInterrupt: 0x1a,
    TIFR: 0x36,
    OCRA: 0x88,
    OCRB: 0x8a,
    ICR: 0x86,
    TCNT: 0x84,
    TCCRA: 0x80,
    TCCRB: 0x81,
    TCCRC: 0x82,
    TIMSK: 0x6f,
    dividers: timer01Dividers,
    compPortA: portBConfig.PORT,
    compPinA: 1,
    compPortB: portBConfig.PORT,
    compPinB: 2,
};
export const timer2Config = {
    bits: 8,
    captureInterrupt: 0,
    compAInterrupt: 0x0e,
    compBInterrupt: 0x10,
    ovfInterrupt: 0x12,
    TIFR: 0x37,
    OCRA: 0xb3,
    OCRB: 0xb4,
    ICR: 0,
    TCNT: 0xb2,
    TCCRA: 0xb0,
    TCCRB: 0xb1,
    TCCRC: 0,
    TIMSK: 0x70,
    dividers: {
        0: 0,
        1: 1,
        2: 8,
        3: 32,
        4: 64,
        5: 128,
        6: 256,
        7: 1024,
    },
    compPortA: portBConfig.PORT,
    compPinA: 3,
    compPortB: portDConfig.PORT,
    compPinB: 3,
};
/* All the following types and constants are related to WGM (Waveform Generation Mode) bits: */
var TimerMode;
(function (TimerMode) {
    TimerMode[TimerMode["Normal"] = 0] = "Normal";
    TimerMode[TimerMode["PWMPhaseCorrect"] = 1] = "PWMPhaseCorrect";
    TimerMode[TimerMode["CTC"] = 2] = "CTC";
    TimerMode[TimerMode["FastPWM"] = 3] = "FastPWM";
    TimerMode[TimerMode["PWMPhaseFrequencyCorrect"] = 4] = "PWMPhaseFrequencyCorrect";
    TimerMode[TimerMode["Reserved"] = 5] = "Reserved";
})(TimerMode || (TimerMode = {}));
var TOVUpdateMode;
(function (TOVUpdateMode) {
    TOVUpdateMode[TOVUpdateMode["Max"] = 0] = "Max";
    TOVUpdateMode[TOVUpdateMode["Top"] = 1] = "Top";
    TOVUpdateMode[TOVUpdateMode["Bottom"] = 2] = "Bottom";
})(TOVUpdateMode || (TOVUpdateMode = {}));
var OCRUpdateMode;
(function (OCRUpdateMode) {
    OCRUpdateMode[OCRUpdateMode["Immediate"] = 0] = "Immediate";
    OCRUpdateMode[OCRUpdateMode["Top"] = 1] = "Top";
    OCRUpdateMode[OCRUpdateMode["Bottom"] = 2] = "Bottom";
})(OCRUpdateMode || (OCRUpdateMode = {}));
const TopOCRA = 1;
const TopICR = 2;
const wgmModes8Bit = [
    /*0*/ [TimerMode.Normal, 0xff, OCRUpdateMode.Immediate, TOVUpdateMode.Max],
    /*1*/ [TimerMode.PWMPhaseCorrect, 0xff, OCRUpdateMode.Top, TOVUpdateMode.Bottom],
    /*2*/ [TimerMode.CTC, TopOCRA, OCRUpdateMode.Immediate, TOVUpdateMode.Max],
    /*3*/ [TimerMode.FastPWM, 0xff, OCRUpdateMode.Bottom, TOVUpdateMode.Max],
    /*4*/ [TimerMode.Reserved, 0xff, OCRUpdateMode.Immediate, TOVUpdateMode.Max],
    /*5*/ [TimerMode.PWMPhaseCorrect, TopOCRA, OCRUpdateMode.Top, TOVUpdateMode.Bottom],
    /*6*/ [TimerMode.Reserved, 0xff, OCRUpdateMode.Immediate, TOVUpdateMode.Max],
    /*7*/ [TimerMode.FastPWM, TopOCRA, OCRUpdateMode.Bottom, TOVUpdateMode.Top],
];
// Table 16-4 in the datasheet
const wgmModes16Bit = [
    /*0 */ [TimerMode.Normal, 0xffff, OCRUpdateMode.Immediate, TOVUpdateMode.Max],
    /*1 */ [TimerMode.PWMPhaseCorrect, 0x00ff, OCRUpdateMode.Top, TOVUpdateMode.Bottom],
    /*2 */ [TimerMode.PWMPhaseCorrect, 0x01ff, OCRUpdateMode.Top, TOVUpdateMode.Bottom],
    /*3 */ [TimerMode.PWMPhaseCorrect, 0x03ff, OCRUpdateMode.Top, TOVUpdateMode.Bottom],
    /*4 */ [TimerMode.CTC, TopOCRA, OCRUpdateMode.Immediate, TOVUpdateMode.Max],
    /*5 */ [TimerMode.FastPWM, 0x00ff, OCRUpdateMode.Bottom, TOVUpdateMode.Top],
    /*6 */ [TimerMode.FastPWM, 0x01ff, OCRUpdateMode.Bottom, TOVUpdateMode.Top],
    /*7 */ [TimerMode.FastPWM, 0x03ff, OCRUpdateMode.Bottom, TOVUpdateMode.Top],
    /*8 */ [TimerMode.PWMPhaseFrequencyCorrect, TopICR, OCRUpdateMode.Bottom, TOVUpdateMode.Bottom],
    /*9 */ [TimerMode.PWMPhaseFrequencyCorrect, TopOCRA, OCRUpdateMode.Bottom, TOVUpdateMode.Bottom],
    /*10*/ [TimerMode.PWMPhaseCorrect, TopICR, OCRUpdateMode.Top, TOVUpdateMode.Bottom],
    /*11*/ [TimerMode.PWMPhaseCorrect, TopOCRA, OCRUpdateMode.Top, TOVUpdateMode.Bottom],
    /*12*/ [TimerMode.CTC, TopICR, OCRUpdateMode.Immediate, TOVUpdateMode.Max],
    /*13*/ [TimerMode.Reserved, 0xffff, OCRUpdateMode.Immediate, TOVUpdateMode.Max],
    /*14*/ [TimerMode.FastPWM, TopICR, OCRUpdateMode.Bottom, TOVUpdateMode.Top],
    /*15*/ [TimerMode.FastPWM, TopOCRA, OCRUpdateMode.Bottom, TOVUpdateMode.Top],
];
function compToOverride(comp) {
    switch (comp) {
        case 1:
            return PinOverrideMode.Toggle;
        case 2:
            return PinOverrideMode.Clear;
        case 3:
            return PinOverrideMode.Set;
        default:
            return PinOverrideMode.Enable;
    }
}
export class AVRTimer {
    constructor(cpu, config) {
        this.cpu = cpu;
        this.config = config;
        this.lastCycle = 0;
        this.ocrA = 0;
        this.ocrB = 0;
        this.icr = 0; // only for 16-bit timers
        this.tcnt = 0;
        this.tcntUpdated = false;
        this.countingUp = true;
        // This is the temporary register used to access 16-bit registers (section 16.3 of the datasheet)
        this.highByteTemp = 0;
        this.updateWGMConfig();
        this.cpu.readHooks[config.TCNT] = (addr) => {
            this.tick();
            if (this.config.bits === 16) {
                this.cpu.data[addr + 1] = this.tcnt >> 8;
            }
            return (this.cpu.data[addr] = this.tcnt & 0xff);
        };
        this.cpu.writeHooks[config.TCNT] = (value) => {
            this.tcnt = (this.highByteTemp << 8) | value;
            this.tcntUpdated = true;
            this.timerUpdated();
        };
        this.cpu.writeHooks[config.OCRA] = (value) => {
            // TODO implement buffering when timer running in PWM mode
            this.ocrA = (this.highByteTemp << 8) | value;
        };
        this.cpu.writeHooks[config.OCRB] = (value) => {
            // TODO implement buffering when timer running in PWM mode
            this.ocrB = (this.highByteTemp << 8) | value;
        };
        this.cpu.writeHooks[config.ICR] = (value) => {
            this.icr = (this.highByteTemp << 8) | value;
        };
        if (this.config.bits === 16) {
            const updateTempRegister = (value) => {
                this.highByteTemp = value;
            };
            this.cpu.writeHooks[config.TCNT + 1] = updateTempRegister;
            this.cpu.writeHooks[config.OCRA + 1] = updateTempRegister;
            this.cpu.writeHooks[config.OCRB + 1] = updateTempRegister;
            this.cpu.writeHooks[config.ICR + 1] = updateTempRegister;
        }
        cpu.writeHooks[config.TCCRA] = (value) => {
            this.cpu.data[config.TCCRA] = value;
            this.compA = ((value >> 6) & 0x3);
            this.updateCompA(this.compA ? PinOverrideMode.Enable : PinOverrideMode.None);
            this.compB = ((value >> 4) & 0x3);
            this.updateCompB(this.compB ? PinOverrideMode.Enable : PinOverrideMode.None);
            this.updateWGMConfig();
            return true;
        };
        cpu.writeHooks[config.TCCRB] = (value) => {
            this.cpu.data[config.TCCRB] = value;
            this.updateWGMConfig();
            return true;
        };
    }
    reset() {
        this.lastCycle = 0;
        this.ocrA = 0;
        this.ocrB = 0;
    }
    get TIFR() {
        return this.cpu.data[this.config.TIFR];
    }
    set TIFR(value) {
        this.cpu.data[this.config.TIFR] = value;
    }
    get TCCRA() {
        return this.cpu.data[this.config.TCCRA];
    }
    get TCCRB() {
        return this.cpu.data[this.config.TCCRB];
    }
    get TIMSK() {
        return this.cpu.data[this.config.TIMSK];
    }
    get CS() {
        return (this.TCCRB & 0x7);
    }
    get WGM() {
        const mask = this.config.bits === 16 ? 0x18 : 0x8;
        return ((this.TCCRB & mask) >> 1) | (this.TCCRA & 0x3);
    }
    get TOP() {
        switch (this.topValue) {
            case TopOCRA:
                return this.ocrA;
            case TopICR:
                return this.icr;
            default:
                return this.topValue;
        }
    }
    updateWGMConfig() {
        const wgmModes = this.config.bits === 16 ? wgmModes16Bit : wgmModes8Bit;
        const [timerMode, topValue] = wgmModes[this.WGM];
        this.timerMode = timerMode;
        this.topValue = topValue;
    }
    tick() {
        const divider = this.config.dividers[this.CS];
        const delta = this.cpu.cycles - this.lastCycle;
        if (divider && delta >= divider) {
            const counterDelta = Math.floor(delta / divider);
            this.lastCycle += counterDelta * divider;
            const val = this.tcnt;
            const { timerMode } = this;
            const phasePwm = timerMode === TimerMode.PWMPhaseCorrect || timerMode === TimerMode.PWMPhaseFrequencyCorrect;
            const newVal = phasePwm
                ? this.phasePwmCount(val, counterDelta)
                : (val + counterDelta) % (this.TOP + 1);
            // A CPU write overrides (has priority over) all counter clear or count operations.
            if (!this.tcntUpdated) {
                this.tcnt = newVal;
                this.timerUpdated();
            }
            if ((timerMode === TimerMode.Normal || timerMode === TimerMode.FastPWM) && val > newVal) {
                this.TIFR |= TOV;
            }
        }
        this.tcntUpdated = false;
        if (this.cpu.interruptsEnabled) {
            const { TIFR, TIMSK } = this;
            if (TIFR & TOV && TIMSK & TOIE) {
                avrInterrupt(this.cpu, this.config.ovfInterrupt);
                this.TIFR &= ~TOV;
            }
            if (TIFR & OCFA && TIMSK & OCIEA) {
                avrInterrupt(this.cpu, this.config.compAInterrupt);
                this.TIFR &= ~OCFA;
            }
            if (TIFR & OCFB && TIMSK & OCIEB) {
                avrInterrupt(this.cpu, this.config.compBInterrupt);
                this.TIFR &= ~OCFB;
            }
        }
    }
    phasePwmCount(value, delta) {
        while (delta > 0) {
            if (this.countingUp) {
                value++;
                if (value === this.TOP && !this.tcntUpdated) {
                    this.countingUp = false;
                }
            }
            else {
                value--;
                if (!value && !this.tcntUpdated) {
                    this.countingUp = true;
                    this.TIFR |= TOV;
                }
            }
            delta--;
        }
        return value;
    }
    timerUpdated() {
        const value = this.tcnt;
        if (this.ocrA && value === this.ocrA) {
            this.TIFR |= OCFA;
            if (this.timerMode === TimerMode.CTC) {
                // Clear Timer on Compare Match (CTC) Mode
                this.tcnt = 0;
                this.TIFR |= TOV;
            }
            if (this.compA) {
                this.updateCompPin(this.compA, 'A');
            }
        }
        if (this.ocrB && value === this.ocrB) {
            this.TIFR |= OCFB;
            if (this.compB) {
                this.updateCompPin(this.compB, 'B');
            }
        }
    }
    updateCompPin(compValue, pinName) {
        let newValue = PinOverrideMode.None;
        const inverted = compValue === 3;
        const isSet = this.countingUp === inverted;
        switch (this.timerMode) {
            case TimerMode.Normal:
            case TimerMode.CTC:
            case TimerMode.FastPWM:
                newValue = compToOverride(compValue);
                break;
            case TimerMode.PWMPhaseCorrect:
            case TimerMode.PWMPhaseFrequencyCorrect:
                newValue = isSet ? PinOverrideMode.Set : PinOverrideMode.Clear;
                break;
        }
        if (newValue !== PinOverrideMode.None) {
            if (pinName === 'A') {
                this.updateCompA(newValue);
            }
            else {
                this.updateCompB(newValue);
            }
        }
    }
    updateCompA(value) {
        const { compPortA, compPinA } = this.config;
        const hook = this.cpu.gpioTimerHooks[compPortA];
        if (hook) {
            hook(compPinA, value, compPortA);
        }
    }
    updateCompB(value) {
        const { compPortB, compPinB } = this.config;
        const hook = this.cpu.gpioTimerHooks[compPortB];
        if (hook) {
            hook(compPinB, value, compPortB);
        }
    }
}
